<script setup lang="ts">
import BaseLabel from '@b2b-platform/base/components/BaseLabel.vue';
import type { PropType } from 'vue';

import type { InputFieldVariant } from '../types/general';

const props = defineProps({
    attrs: {
        default: () => ({}),
        type: Object as PropType<string | any>,
    },
    autocomplete: {
        default: true,
        type: Boolean,
    },
    disabled: {
        default: false,
        type: Boolean,
    },
    error: {
        default: null,
        type: String,
    },
    fullWidth: {
        default: true,
        type: Boolean,
    },
    highlighted: {
        default: false,
        type: Boolean,
    },
    icon: {
        default: null,
        type: String,
    },
    info: {
        default: null,
        type: String,
    },
    infoLabel: {
        default: 'Hinweis',
        type: String,
    },
    infoShow: {
        default: true,
        type: Boolean,
    },
    infoShowFrom: {
        default: '',
        type: String as PropType<'md' | 'lg' | ''>,
    },
    infoShowUntil: {
        default: '',
        type: String as PropType<'md' | 'lg' | ''>,
    },
    label: {
        default: null,
        type: String,
    },
    limit: {
        default: null,
        type: Number,
    },
    max: {
        default: null,
        type: String,
    },
    min: {
        default: null,
        type: String,
    },
    modelValue: {
        default: () => '',
        type: [String, Number, null] as PropType<string | number | null>,
    },
    name: {
        default: 'input',
        type: String,
    },
    pattern: {
        default: null,
        type: String,
    },
    placeholder: {
        default: '',
        type: String,
    },
    readonly: {
        default: false,
        type: Boolean,
    },
    type: {
        default: 'input',
        type: String,
    },
    uppercase: {
        default: false,
        type: Boolean,
    },
    variant: {
        default: 'default',
        type: String as PropType<InputFieldVariant>,
    },
    widthClass: {
        default: '',
        type: String,
    },
});

const emit = defineEmits(['update:modelValue']);

const id = useId();
const value = ref(props.modelValue);

watch(value, () => {
    emit('update:modelValue', value.value);
});

watch(
    () => props.modelValue,
    () => {
        value.value = props.modelValue;
    },
);
</script>

<template>
    <div
        :data-invalid-field="error || null"
        class="block text-left"
        :class="widthClass"
    >
        <BaseLabel
            v-if="label || info"
            :for-id="id"
            :label="label"
            :info="info"
            :info-label="infoLabel"
            :info-show="infoShow"
            :info-show-from="infoShowFrom"
            :info-show-until="infoShowUntil"
            :variant="variant"
        />

        <span
            class="flex items-center gap-1 rounded border border-gray-300 bg-white px-2 has-[input:disabled]:cursor-not-allowed has-[input:active]:border-gray-900 has-[input:focus]:border-gray-900 has-[input:invalid]:border-red-700 has-[input:disabled]:opacity-40 has-[input:focus-visible]:outline has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2 has-[input:focus-visible]:outline-blue-600"
            :class="[
                error && error !== '' && 'border-red-700',
                fullWidth ? 'w-auto' : 'lg:max-w-[210px]',
                highlighted && 'border-yellow-600',
                variant === 'compact' ? 'min-h-[34px] py-1' : 'py-3',
            ]"
        >
            <slot name="before-input"></slot>

            <JamBaseIcon
                v-if="icon"
                :icon-name="icon"
                stroke="thick"
                class="text-gray-600"
            />
            <input
                :id="id"
                v-model="value"
                v-bind="attrs"
                :name="name"
                :type="type"
                :disabled="disabled"
                :placeholder="placeholder"
                :min="min"
                :max="max"
                :maxlength="limit"
                :pattern="pattern"
                :readonly="readonly"
                :autocomplete="autocomplete ? 'on' : 'off'"
                class="w-full grow truncate bg-white px-1 text-gray-900 placeholder:text-gray-600 focus:outline-none focus-visible:outline-none"
                :class="{
                    uppercase: uppercase,
                    'text-sm': variant === 'compact',
                }"
            />

            <slot name="after-input"></slot>
        </span>
        <transition
            enter-active-class="transition-all duration-300 ease-in"
            enter-from-class="max-h-0 opacity-0"
            enter-to-class="max-h-[100px]"
            leave-active-class="transition-all duration-300 ease-out"
            leave-from-class="max-h-[100px]"
            leave-to-class="max-h-0 opacity-0"
        >
            <BaseErrorMessage
                v-if="error"
                :message="error"
                :variant="variant"
            />
        </transition>
    </div>
</template>
